import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, FolderTree, Sparkles, RefreshCw, UploadCloud, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { Modal, IconBtn, AddBtn, inputCls } from "./ui";
import { AISuggestCategoriesModal } from "./AISuggestCategoriesModal";
import { ICON_OPTIONS, iconByName } from "../../lib/categories";

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(null);
  const [ai, setAi] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [branding, setBranding] = useState(false);
  const brandAll = async () => {
    if (!cats.length) return;
    setBranding(true);
    let done = 0;
    for (const c of cats) {
      try { await api.post(`/admin/categories/${c.id}/generate-image`); done += 1; toast.message(`Generated ${done}/${cats.length}…`); } catch { /* skip */ }
    }
    toast.success(`Generated on-brand images for ${done} categor${done === 1 ? "y" : "ies"}`);
    setBranding(false); load();
  };
  const syncCats = async () => {
    setSyncing(true);
    try { await api.post("/admin/categories/sync"); toast.success("Categories synced to the AfriGate taxonomy"); load(); }
    catch { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  };
  const load = async () => { const { data } = await api.get("/admin/categories"); setCats(data); };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this category?")) return; await api.delete(`/admin/categories/${id}`); toast.success("Category deleted"); load(); };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-[#5C5C5C] max-w-xl">Your storefront categories. Products imported from stores are AI-mapped into these automatically — you rarely need to edit here. Use <b>Sync</b> to reset to the standard AfriGate taxonomy.</p>
        <div className="flex gap-2 shrink-0">
          <button onClick={brandAll} disabled={branding} data-testid="brand-categories-btn"
            className="flex items-center gap-1.5 text-sm font-semibold bg-[#D9933B] hover:bg-[#c07f2c] text-white px-4 py-2 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
            <Sparkles size={15} className={branding ? "animate-pulse" : ""} /> {branding ? "Generating…" : "Generate all images"}
          </button>
          <button onClick={syncCats} disabled={syncing} data-testid="sync-categories-btn"
            className="flex items-center gap-1.5 text-sm font-semibold bg-[#306D29] hover:bg-[#0D530E] text-white px-4 py-2 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
            <RefreshCw size={15} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync categories"}
          </button>
          <AddBtn label="Add category" onClick={() => setForm({})} testid="add-category-btn" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cats.length === 0 && <p className="text-[#5C5C5C]">No categories yet.</p>}
        {cats.map((c) => (
          <div key={c.id} className="bg-white border border-black/5 rounded-2xl p-5" data-testid={`category-row-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-11 h-11 rounded-xl overflow-hidden bg-[#306D29]/10 text-[#306D29] flex items-center justify-center shrink-0">
                  {c.image ? <img src={c.image} alt="" className="w-full h-full object-cover" /> : (() => { const I = iconByName(c.icon) || FolderTree; return <I size={18} />; })()}
                </span>
                <div className="min-w-0">
                  <p className="font-display text-lg text-[#0D530E] truncate">{c.name}</p>
                  <p className="text-xs text-[#5C5C5C]">{c.product_count} products</p>
                </div>
              </div>
              <div className="flex gap-0.5 shrink-0">
                <IconBtn title="Edit" testid={`cat-edit-${c.id}`} onClick={() => setForm(c)}><Pencil size={15} /></IconBtn>
                <IconBtn title="Delete" danger testid={`cat-del-${c.id}`} onClick={() => del(c.id)}><Trash2 size={15} /></IconBtn>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {(c.subcategories || []).length === 0 && <span className="text-xs text-[#5C5C5C]">No sub-categories</span>}
              {(c.subcategories || []).map((s) => <span key={s} className="text-xs bg-[#E7E1B1]/60 text-[#0D530E] rounded-full px-2.5 py-1">{s}</span>)}
            </div>
          </div>
        ))}
      </div>
      {form && <CategoryForm cat={form} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
      {ai && <AISuggestCategoriesModal onClose={() => setAi(false)} onApplied={() => { setAi(false); load(); }} />}
    </div>
  );
}

const ICON_LIST = Object.keys(ICON_OPTIONS);

function CategoryForm({ cat, onClose, onSaved }) {
  const editing = !!cat.id;
  const [f, setF] = useState({ name: cat.name || "", blurb: cat.blurb || "", image: cat.image || "", icon: cat.icon || "", subs: (cat.subcategories || []).join(", ") });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [genning, setGenning] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, image: data.url })); toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };
  const genImage = async () => {
    if (!editing) { toast.error("Save the category first, then generate"); return; }
    setGenning(true);
    try {
      const { data } = await api.post(`/admin/categories/${cat.id}/generate-image`);
      setF((p) => ({ ...p, image: data.image })); toast.success("Image generated from this category's products");
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); } finally { setGenning(false); }
  };
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    const body = { name: f.name, blurb: f.blurb, image: f.image, icon: f.icon, subcategories: f.subs.split(",").map((s) => s.trim()).filter(Boolean), active: true };
    try {
      if (editing) await api.put(`/admin/categories/${cat.id}`, body); else await api.post("/admin/categories", body);
      toast.success(editing ? "Category saved" : "Category added"); onSaved();
    } catch { toast.error("Failed to save category"); setSaving(false); }
  };

  return (
    <Modal title={editing ? "Edit category" : "Add category"} onClose={onClose} testid="category-form">
      <form onSubmit={submit} className="space-y-4">
        <input required className={inputCls} placeholder="Category name" value={f.name} onChange={set("name")} data-testid="cf-name" />
        <input className={inputCls} placeholder="Short blurb shown on the category page" value={f.blurb} onChange={set("blurb")} data-testid="cf-blurb" />

        <div>
          <label className="text-xs font-semibold text-[#5C5C5C]">Category image (placeholder shown on the storefront)</label>
          {f.image && <img src={f.image} alt="" className="w-full h-36 object-cover rounded-xl bg-[#E7E1B1] mt-1.5" data-testid="cf-image-preview" />}
          <input className={`${inputCls} mt-2`} placeholder="Image URL" value={f.image} onChange={set("image")} data-testid="cf-image" />
          <div className="flex gap-2 mt-2">
            <label className="flex-1 cursor-pointer flex items-center justify-center gap-1.5 text-sm font-medium border border-[#306D29]/30 text-[#306D29] rounded-xl py-2 hover:bg-[#306D29]/5">
              <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="cf-upload" />
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />} {uploading ? "Uploading…" : "Upload"}
            </label>
            <button type="button" onClick={genImage} disabled={genning} data-testid="cf-generate"
              className="flex-1 flex items-center justify-center gap-1.5 text-sm font-medium border border-[#306D29]/30 text-[#306D29] rounded-xl py-2 hover:bg-[#306D29]/5 disabled:opacity-60">
              {genning ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {genning ? "Generating…" : "Generate with AI"}
            </button>
          </div>
          {!editing && <p className="text-[11px] text-[#5C5C5C] mt-1">Save first to enable AI generation from this category's products.</p>}
        </div>

        <div>
          <label className="text-xs font-semibold text-[#5C5C5C]">Icon</label>
          <div className="grid grid-cols-7 gap-2 mt-1.5" data-testid="cf-icon-grid">
            {ICON_LIST.map((nm) => { const I = ICON_OPTIONS[nm]; return (
              <button key={nm} type="button" onClick={() => setF({ ...f, icon: nm })} data-testid={`cf-icon-${nm}`}
                title={nm} className={`aspect-square rounded-xl flex items-center justify-center border ${f.icon === nm ? "bg-[#306D29] text-white border-[#306D29]" : "bg-white text-[#5C5C5C] border-black/10 hover:border-[#306D29]/40"}`} style={{ transition: "background-color 0.2s ease" }}>
                <I size={18} />
              </button>
            ); })}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-[#5C5C5C]">Sub-categories (comma separated)</label>
          <textarea className={inputCls} rows={2} placeholder="Breakfast, Snacks, Drinks" value={f.subs} onChange={set("subs")} data-testid="cf-subs" />
        </div>
        <button type="submit" disabled={saving} data-testid="cf-submit" className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>{saving ? "Saving…" : editing ? "Save category" : "Add category"}</button>
      </form>
    </Modal>
  );
}
